import { GoogleGenAI, Type } from "@google/genai";
// FIX: Import StreamingAnalysis type.
import { SpeedTestResult, HistoricalTestResult, ConnectionInfo, AIAnalysisResult, RecommendationsResult, StreamingAnalysis } from '../types';
import { Server } from 'shared-types';

let ai: GoogleGenAI | undefined;

if (process.env.API_KEY) {
  ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
}

export async function analyzeSpeedTestResults(
  results: SpeedTestResult,
  history: HistoricalTestResult[],
  connectionInfo: ConnectionInfo,
  server: Server,
  deviceInfo: string
): Promise<AIAnalysisResult> {

  if (!ai) {
    throw new Error("AI features are disabled. The API key is not configured in this environment.");
  }

  const historyString = history.length > 0
    ? history.map(h => `  - D: ${h.download.toFixed(1)} Mbps, U: ${h.upload.toFixed(1)} Mbps, P: ${h.ping}ms`).join('\n')
    : "  - No recent history available.";

  const prompt = `
Analyze the provided internet speed test results. Your goal is to provide a concise, easy-to-understand analysis by filling out the provided JSON schema.

- Be friendly and helpful, but direct.
- Base ratings on typical home internet user needs (streaming, gaming, video calls).
- For activities, list 2-3 common examples.
- For the history trend, only comment if there's a clear and significant pattern in the last few tests. If not, omit the field.

Here is the data:
- Current Test Results:
  - Download: ${results.download.toFixed(2)} Mbps
  - Upload: ${results.upload.toFixed(2)} Mbps
  - Ping: ${results.ping} ms
  - Jitter: ${results.jitter} ms
- ISP: ${connectionInfo.isp}
- Device: ${deviceInfo}
- Test Server: ${server.name}, ${server.location}
- Recent Test History (last ${history.length} tests, oldest first):
${historyString}
`;

  const responseSchema = {
    type: Type.OBJECT,
    properties: {
      summary: { type: Type.STRING, description: 'A single, encouraging sentence summarizing the overall result.' },
      download: {
        type: Type.OBJECT,
        properties: {
          rating: { type: Type.STRING, enum: ['Excellent', 'Good', 'Fair', 'Poor'], description: 'A one-word rating for the download speed.' },
          description: { type: Type.STRING, description: 'A brief sentence explaining the rating.' },
          activities: { type: Type.ARRAY, items: { type: Type.STRING }, description: 'A list of 2-3 activities this speed is good for.' }
        },
        required: ['rating', 'description', 'activities']
      },
      upload: {
        type: Type.OBJECT,
        properties: {
          rating: { type: Type.STRING, enum: ['Excellent', 'Good', 'Fair', 'Poor'], description: 'A one-word rating for the upload speed.' },
          description: { type: Type.STRING, description: 'A brief sentence explaining the rating.' },
          activities: { type: Type.ARRAY, items: { type: Type.STRING }, description: 'A list of 2-3 activities this speed is good for.' }
        },
        required: ['rating', 'description', 'activities']
      },
      latency: {
        type: Type.OBJECT,
        properties: {
          rating: { type: Type.STRING, enum: ['Excellent', 'Good', 'Fair', 'Poor'], description: 'A one-word rating for the latency (ping and jitter).' },
          pingDescription: { type: Type.STRING, description: 'A brief sentence explaining the ping result.' },
          jitterDescription: { type: Type.STRING, description: 'A brief sentence explaining the jitter result.' }
        },
        required: ['rating', 'pingDescription', 'jitterDescription']
      },
      historyTrend: { type: Type.STRING, description: 'An optional brief sentence on performance trends if any are obvious from the history.' }
    },
    required: ['summary', 'download', 'upload', 'latency']
  };

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: prompt,
      config: {
        responseMimeType: "application/json",
        responseSchema: responseSchema,
      },
    });
    
    return JSON.parse(response.text) as AIAnalysisResult;

  } catch (error) {
    console.error("Gemini API call failed:", error);
    if (error instanceof Error && error.message.includes('API key')) {
       throw new Error("Failed to get analysis: Invalid API Key provided.");
    }
    if (error instanceof SyntaxError) {
        throw new Error("Failed to get analysis from AI. The response was not valid JSON.");
    }
    throw new Error("Failed to get analysis from AI. The service might be temporarily unavailable.");
  }
}

export async function getRecommendations(
  results: SpeedTestResult
): Promise<RecommendationsResult> {
  if (!ai) {
    throw new Error("AI features are disabled. The API key is not configured in this environment.");
  }

  const prompt = `
Based on the following internet speed test results, provide a set of actionable recommendations.
The recommendations should be categorized for different user types.
Provide 2-3 concise, helpful, and distinct recommendations for each category.
- If the speed is low, suggest troubleshooting steps.
- If the speed is high, suggest ways to make the most of it.
- Make the tone encouraging and easy for a non-technical user to understand.

Speed Test Results:
- Download: ${results.download.toFixed(2)} Mbps
- Upload: ${results.upload.toFixed(2)} Mbps
- Ping: ${results.ping} ms
- Jitter: ${results.jitter} ms

Fill out the JSON schema with your recommendations.
`;

  const responseSchema = {
    type: Type.OBJECT,
    properties: {
      general: {
        type: Type.ARRAY,
        description: "General tips for any user to improve or maintain their connection.",
        items: {
          type: Type.OBJECT,
          properties: {
            title: { type: Type.STRING, description: "A short, catchy title for the tip." },
            description: { type: Type.STRING, description: "A one-sentence description of the recommendation." }
          },
          required: ['title', 'description']
        }
      },
      gaming: {
        type: Type.ARRAY,
        description: "Tips specifically for online gamers, focusing on latency and stability.",
        items: {
          type: Type.OBJECT,
          properties: {
            title: { type: Type.STRING, description: "A short, catchy title for the tip." },
            description: { type: Type.STRING, description: "A one-sentence description of the recommendation." }
          },
          required: ['title', 'description']
        }
      },
      streaming: {
        type: Type.ARRAY,
        description: "Tips for users who stream video content (e.g., Netflix, YouTube).",
        items: {
          type: Type.OBJECT,
          properties: {
            title: { type: Type.STRING, description: "A short, catchy title for the tip." },
            description: { type: Type.STRING, description: "A one-sentence description of the recommendation." }
          },
          required: ['title', 'description']
        }
      },
      remoteWork: {
        type: Type.ARRAY,
        description: "Tips for people working from home, focusing on video conferencing and file transfers.",
        items: {
          type: Type.OBJECT,
          properties: {
            title: { type: Type.STRING, description: "A short, catchy title for the tip." },
            description: { type: Type.STRING, description: "A one-sentence description of the recommendation." }
          },
          required: ['title', 'description']
        }
      }
    },
    required: ['general', 'gaming', 'streaming', 'remoteWork']
  };

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: prompt,
      config: {
        responseMimeType: "application/json",
        responseSchema: responseSchema,
      },
    });
    
    return JSON.parse(response.text) as RecommendationsResult;
  } catch (error) {
    console.error("Gemini API call for recommendations failed:", error);
     if (error instanceof Error && error.message.includes('API key')) {
       throw new Error("Failed to get recommendations: Invalid API Key provided.");
    }
    if (error instanceof SyntaxError) {
        throw new Error("Failed to get recommendations from AI. The response was not valid JSON.");
    }
    throw new Error("Failed to get recommendations from AI. The service might be temporarily unavailable.");
  }
}

export async function getRelatedSearchQueries(): Promise<string[]> {
    if (!ai) {
        throw new Error("AI features are disabled. The API key is not configured in this environment.");
    }
    
    const prompt = `
        List 8 popular and diverse search queries related to testing internet speed, checking connection quality, and understanding the results. The queries should be things a typical home user would search for on Google.
    `;

    const responseSchema = {
        type: Type.OBJECT,
        properties: {
            queries: {
                type: Type.ARRAY,
                items: { type: Type.STRING },
                description: 'A list of 8 diverse search queries.'
            }
        },
        required: ['queries']
    };

    try {
        const response = await ai.models.generateContent({
            model: 'gemini-2.5-flash',
            contents: prompt,
            config: {
                responseMimeType: "application/json",
                responseSchema: responseSchema,
            },
        });

        const parsed = JSON.parse(response.text);
        return parsed.queries as string[];
    } catch (error) {
        console.error("Gemini API call for search queries failed:", error);
        if (error instanceof Error && error.message.includes('API key')) {
           throw new Error("Failed to get search queries: Invalid API Key provided.");
        }
        if (error instanceof SyntaxError) {
            throw new Error("Failed to get search queries from AI. The response was not valid JSON.");
        }
        throw new Error("Failed to get search queries from AI. The service might be temporarily unavailable.");
    }
}

// FIX: Add analyzeStreamingQuality function to provide AI-powered analysis for video streaming.
export async function analyzeStreamingQuality(
  results: SpeedTestResult
): Promise<StreamingAnalysis[]> {
  if (!ai) {
    throw new Error("AI features are disabled. The API key is not configured in this environment.");
  }

  const prompt = `
Analyze the provided internet speed test results specifically for video streaming quality. 
Your goal is to assess the likely user experience for various common streaming resolutions (4K, 1080p, 720p, SD).
Consider download speed for buffering, and latency (ping/jitter) for stability and start-up time.
Provide a concise analysis by filling out the provided JSON schema.

Speed Test Results:
- Download: ${results.download.toFixed(2)} Mbps
- Ping: ${results.ping} ms
- Jitter: ${results.jitter} ms

Based on these results, evaluate the streaming quality for each resolution.
`;

  const responseSchema = {
    type: Type.ARRAY,
    items: {
        type: Type.OBJECT,
        properties: {
            resolution: { type: Type.STRING, enum: ['4K UHD', '1080p HD', '720p HD', 'SD'], description: "The video streaming resolution." },
            quality: { type: Type.STRING, enum: ['Excellent', 'Good', 'Fair', 'Poor'], description: "A one-word rating for the streaming experience at this resolution." },
            description: { type: Type.STRING, description: "A brief sentence explaining the rating, e.g., 'Smooth streaming with no buffering expected' or 'May experience occasional buffering'." }
        },
        required: ['resolution', 'quality', 'description']
    }
  };
  
  try {
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: prompt,
      config: {
        responseMimeType: "application/json",
        responseSchema: responseSchema,
      },
    });
    
    return JSON.parse(response.text) as StreamingAnalysis[];

  } catch (error) {
    console.error("Gemini API call for streaming analysis failed:", error);
    if (error instanceof Error && error.message.includes('API key')) {
       throw new Error("Failed to get analysis: Invalid API Key provided.");
    }
    if (error instanceof SyntaxError) {
        throw new Error("Failed to get analysis from AI. The response was not valid JSON.");
    }
    throw new Error("Failed to get analysis from AI. The service might be temporarily unavailable.");
  }
}
